import { Add, Edit } from '@mui/icons-material';
import { Divider, FormControl, FormHelperText, Grid, MenuItem, Select, Stack, Typography } from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import { MuiSelectWithFocus } from '../../config/MaterialUIConstants';
import useGlobalStore from '../../store/useGlobalStore';

export const normalizeTermId = (id) => {
    if (id == null || id === '') return '';
    if (typeof id === 'object' && id._id != null) return String(id._id);
    return String(id);
};

export const resolveTermsOptionId = (termsValue, options = []) => {
    if (!termsValue?.title) return '';

    const valueId = normalizeTermId(termsValue._id);
    if (valueId && options.some((o) => normalizeTermId(o._id) === valueId)) {
        return valueId;
    }

    const exactMatch = options.find((o) => o?.title === termsValue.title && o?.description === termsValue.description);
    if (exactMatch?._id) return normalizeTermId(exactMatch._id);

    const titleMatches = options.filter((o) => o?.title === termsValue.title);
    if (titleMatches.length === 1) return normalizeTermId(titleMatches[0]._id);

    return '';
};

export const termIsInvoiceDefault = (item) => !!(item?.isInvoiceDefault ?? item?.isDefaultInvoice);

export const termIsEstimateDefault = (item) => !!(item?.isEstimateDefault ?? item?.isDefaultEstimate);

export const toTermsFormValue = (item) => {
    if (!item?.title || !item?.description) return null;
    const id = normalizeTermId(item._id ?? item.termsAndWarrantyId);
    return {
        ...(id ? { _id: id } : {}),
        title: item.title,
        description: item.description,
    };
};

const TermsAndWarrantySelect = ({ control, errors, options = [], onNew, onEdit, setValue, type = null, invoiceDataInStore, setInvoiceDataInStore }) => {
    const { isDirty, markSaved } = useGlobalStore();
    const termsValue = useWatch({ control, name: 'termsAndWarranty' });
    const selectedId = resolveTermsOptionId(termsValue, options);
    const canEdit = !!selectedId;

    const applySelection = (item) => {
        const next = toTermsFormValue(item);

        setValue('termsAndWarranty', next);

        if (type === 'invoice') {
            if (!item) {
                const { termsAndWarranty, ...rest } = invoiceDataInStore || {};
                setInvoiceDataInStore?.(rest);
            } else {
                setInvoiceDataInStore?.({
                    ...invoiceDataInStore,
                    termsAndWarranty: next,
                });
            }
        } else if (!isDirty) {
            markSaved();
        }
    };

    return (
        <Grid item xs={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography className="formLabelTitle" mb={1}>
                    Terms and Warranty
                </Typography>
                <Grid item display="flex" alignItems="center" gap={0}>
                    {canEdit && onEdit && (
                        <>
                            <Stack direction="row" alignItems="center" spacing={0} onClick={() => onEdit?.()} sx={{ cursor: 'pointer' }}>
                                <Edit color="secondary" sx={{ width: '16px', height: '16px', mr: '6px' }} />
                                <Typography color="secondary" fontWeight={500} fontSize="15px" sx={{ cursor: 'pointer' }}>
                                    Edit
                                </Typography>
                            </Stack>
                            <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 1, height: '20px' }} />
                        </>
                    )}
                    {onNew && (
                        <Stack direction="row" alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => onNew?.()}>
                            <Add color="secondary" sx={{ mr: '2px' }} />
                            <Typography color="secondary" fontWeight={500} fontSize="15px" sx={{ cursor: 'pointer' }}>
                                New
                            </Typography>
                        </Stack>
                    )}
                </Grid>
            </div>
            <Controller
                name="termsAndWarranty"
                control={control}
                render={() => (
                    <FormControl fullWidth error={!!errors?.termsAndWarranty}>
                        <Select
                            id="terms-and-warranty-select"
                            value={selectedId}
                            displayEmpty
                            onChange={(e) => {
                                const id = e.target.value;
                                if (!id) {
                                    applySelection(null);
                                    return;
                                }
                                const item = options.find((o) => normalizeTermId(o._id) === id);
                                if (item) applySelection(item);
                            }}
                            MenuProps={{ style: { maxHeight: '400px' } }}
                            sx={{
                                ...MuiSelectWithFocus,
                                height: '50px',
                                boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.06)',
                            }}
                        >
                            <MenuItem value="">None</MenuItem>
                            {options.map((item) => {
                                const optionId = normalizeTermId(item._id);
                                if (!optionId) return null;
                                return (
                                    <MenuItem key={optionId} value={optionId}>
                                        {item.title}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                        {errors?.termsAndWarranty && <FormHelperText>{errors.termsAndWarranty.message}</FormHelperText>}
                    </FormControl>
                )}
            />
        </Grid>
    );
};

export default TermsAndWarrantySelect;
